import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { Loader2, Download, Share2, Trophy, GraduationCap, Target, Twitter } from "lucide-react";
import { celebrate } from "@/components/Celebrate";

const RIASEC_COLORS = { R: "#3B82F6", I: "#8B5CF6", A: "#FB7185", S: "#34D399", E: "#FBBF24", C: "#F97316" };
const CODE_FULL = { R: "Realistic", I: "Investigative", A: "Artistic", S: "Social", E: "Enterprising", C: "Conventional" };

const THEMES = [
  { id: "sunset", label: "Sunset", from: "#FB7185", to: "#FBBF24", accent: "#FEF3C7", text: "#3F0E19" },
  { id: "ocean", label: "Ocean", from: "#0EA5E9", to: "#8B5CF6", accent: "#E0F2FE", text: "#0C4A6E" },
  { id: "mint", label: "Mint", from: "#34D399", to: "#3B82F6", accent: "#ECFDF5", text: "#064E3B" },
  { id: "midnight", label: "Midnight", from: "#0F1B33", to: "#1E293B", accent: "#FBBF24", text: "#F8FAFC" },
];

export default function CareerCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(THEMES[0]);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get("/career-card/").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: null, scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `brimi-career-card-${(data?.name || "student").replace(/\s+/g, "-").toLowerCase()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Career card downloaded!");
      celebrate("Card downloaded!");
      api.post("/achievements/track", { action: "card_download" }).catch(() => {});
    } catch (e) {
      toast.error("Download failed");
    } finally { setDownloading(false); }
  };

  const shareTwitter = () => {
    const text = `I just built my career roadmap on BrimiAI CareerCoach! 🎓 My target: ${data?.target_career || "TBD"}. RIASEC top: ${(data?.top_codes || []).join(" · ")}. #BrimiAI #CareerJourney`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
  };

  const shareWhatsApp = () => {
    const text = `Check out my career plan on BrimiAI CareerCoach 🎓\nTarget: ${data?.target_career || "TBD"}\nRIASEC: ${(data?.top_codes || []).join(" · ")}\nReadiness: ${data?.readiness_score}%`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (loading) return <AppShell><div className="p-10 text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="career-card-page">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Career Card</p>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Your shareable career card</h1>
        <p className="text-slate-400 mt-2 max-w-xl">A polished 1080×1080 image to post on Instagram Stories, WhatsApp status, LinkedIn, or anywhere.</p>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          {/* Card preview */}
          <div className="lg:col-span-8 flex justify-center">
            <div className="w-full max-w-[540px] aspect-square" data-testid="career-card-preview">
              <CardVisual data={data} theme={theme} innerRef={cardRef} />
            </div>
          </div>

          {/* Controls */}
          <div className="lg:col-span-4 space-y-4">
            <div className="brimi-card p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Theme</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {THEMES.map((t) => (
                  <button key={t.id} data-testid={`theme-${t.id}`} onClick={() => setTheme(t)}
                    className={`p-3 rounded-xl border-2 transition ${theme.id === t.id ? "border-slate-900" : "border-slate-200"}`}
                    style={{ background: `linear-gradient(135deg, ${t.from}, ${t.to})` }}>
                    <p className="text-xs font-bold" style={{ color: t.text }}>{t.label}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="brimi-card p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Share</p>
              <div className="mt-3 space-y-2">
                <button data-testid="card-download-btn" onClick={downloadPng} disabled={downloading} className="w-full brimi-pill accent-coral flex items-center justify-center gap-2 disabled:opacity-60">
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download PNG
                </button>
                <button data-testid="card-share-twitter" onClick={shareTwitter} className="w-full py-2.5 rounded-full font-bold border border-slate-200 hover:bg-slate-50 flex items-center justify-center gap-2 text-slate-800 text-sm">
                  <Twitter className="w-4 h-4" /> Share on Twitter
                </button>
                <button data-testid="card-share-whatsapp" onClick={shareWhatsApp} className="w-full py-2.5 rounded-full font-bold border border-slate-200 hover:bg-slate-50 flex items-center justify-center gap-2 text-slate-800 text-sm">
                  <Share2 className="w-4 h-4" /> Share on WhatsApp
                </button>
              </div>
            </div>

            <div className="brimi-card p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Card includes</p>
              <ul className="mt-3 space-y-1 text-sm text-slate-700 list-disc list-inside">
                <li>Your name & grade</li>
                <li>Target career</li>
                <li>RIASEC top codes</li>
                <li>Top college target</li>
                <li>Readiness score badge</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function CardVisual({ data, theme, innerRef }) {
  const codes = data?.top_codes || [];
  return (
    <div ref={innerRef} className="w-full h-full rounded-3xl relative overflow-hidden shadow-2xl" style={{
      background: `linear-gradient(135deg, ${theme.from} 0%, ${theme.to} 100%)`,
      color: theme.text,
    }}>
      {/* Decorative shapes */}
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-30" style={{ background: theme.accent }} />
      <div className="absolute -bottom-20 -left-10 w-48 h-48 rounded-full opacity-20" style={{ background: theme.accent }} />

      <div className="relative z-10 p-8 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: theme.accent }}>
              <svg viewBox="0 0 40 40" width="26" height="26" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id={`cc-sun-${theme.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={theme.from} />
                    <stop offset="100%" stopColor={theme.to} />
                  </linearGradient>
                </defs>
                <g stroke={theme.from} strokeWidth="1.5" strokeLinecap="round">
                  <line x1="20" y1="6" x2="20" y2="9" />
                  <line x1="11" y1="10" x2="13" y2="12" />
                  <line x1="29" y1="10" x2="27" y2="12" />
                  <line x1="6" y1="18" x2="9" y2="18" />
                  <line x1="34" y1="18" x2="31" y2="18" />
                </g>
                <path d="M11 24 A 9 9 0 0 1 29 24 Z" fill={`url(#cc-sun-${theme.id})`} />
                <line x1="6" y1="24.5" x2="34" y2="24.5" stroke={theme.from} strokeWidth="0.8" strokeLinecap="round" opacity="0.85" />
                <path d="M6 29 Q 12 26.5, 18 29 T 34 29" stroke={theme.from} strokeWidth="1.4" fill="none" strokeLinecap="round" />
                <path d="M6 33.5 Q 14 31, 22 33.5 T 36 33.5" stroke={theme.from} strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.55" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-sm leading-none" style={{ color: theme.text }}>BrimiAI</p>
              <p className="text-[9px] uppercase tracking-[0.2em] opacity-70 leading-none mt-0.5">CareerCoach</p>
            </div>
          </div>
          <div className="px-3 py-1 rounded-full text-[10px] font-bold" style={{ background: theme.accent, color: theme.from }}>
            {data?.readiness_score || 0}% READY
          </div>
        </div>

        {/* Name */}
        <div className="mt-8">
          <p className="text-xs uppercase tracking-[0.25em] opacity-70 font-bold">Career journey of</p>
          <p className="text-4xl font-black mt-1 leading-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{data?.name || "Student"}</p>
          {data?.grade && <p className="text-sm opacity-80 mt-1">Class {data.grade} · {data.stream || "Undecided stream"}</p>}
        </div>

        {/* Target */}
        <div className="mt-6 p-4 rounded-2xl" style={{ background: `${theme.accent}` }}>
          <div className="flex items-start gap-2">
            <Target className="w-4 h-4 mt-0.5" style={{ color: theme.from }} />
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: theme.from }}>Target career</p>
              <p className="text-lg font-black mt-0.5" style={{ color: theme.from }}>{data?.target_career || "Discovering…"}</p>
            </div>
          </div>
          {data?.top_college && (
            <div className="flex items-start gap-2 mt-3 pt-3 border-t border-black/10">
              <GraduationCap className="w-4 h-4 mt-0.5" style={{ color: theme.from }} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: theme.from }}>Dream college</p>
                <p className="text-sm font-bold mt-0.5" style={{ color: theme.from }}>{data.top_college}</p>
              </div>
            </div>
          )}
        </div>

        {/* RIASEC top codes */}
        {codes.length > 0 && (
          <div className="mt-5">
            <p className="text-[10px] uppercase tracking-[0.2em] opacity-70 font-bold">Personality (RIASEC)</p>
            <div className="flex gap-2 mt-2">
              {codes.map((c) => (
                <div key={c} className="flex-1 p-3 rounded-xl text-center" style={{ background: theme.accent }}>
                  <p className="font-black text-2xl" style={{ color: RIASEC_COLORS[c] }}>{c}</p>
                  <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: theme.from }}>{CODE_FULL[c]}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto pt-6 flex items-center justify-between">
          <p className="text-[10px] opacity-70">Built with BrimiAI · Grades IX–XII</p>
          <div className="flex items-center gap-1">
            <Trophy className="w-3 h-3" />
            <p className="text-[10px] font-bold">brimi.ai</p>
          </div>
        </div>
      </div>
    </div>
  );
}
